/**
 * Pool ranking page — uses PageHeader, Card, Avatar, Badge, Skeleton, EmptyState, Alert.
 * Memoized PodiumPlace and RankingRow rows.
 */

import { useState, memo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Trophy, Search, Crown } from 'lucide-react';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/card';
import Badge from '../components/ui/badge';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { usePoolRanking, usePool } from '../hooks/usePools';
import { cn, getInitials, getTrendDisplay, formatPoints } from '../lib/utils';
import type { Ranking } from '../../shared/types';
import { classifyPoolPageErrorState } from './pool-page-error-state';

export default function PoolRankingPage() {
  const { slug } = useParams<{ slug: string }>();
  const poolSlug = slug ?? '';
  const { data: poolData, error: poolError } = usePool(poolSlug);
  const { data: rankings, isLoading, error: rankingError } = usePoolRanking(poolSlug);
  const [search, setSearch] = useState('');

  const pool = poolData?.pool;
  const poolErrorState = classifyPoolPageErrorState({
    hasPool: Boolean(pool),
    error: poolError,
  });

  if (poolErrorState) {
    return (
      <EmptyState
        icon={<Trophy className="w-6 h-6" aria-hidden="true" />}
        title={poolErrorState.title}
        description={poolErrorState.description}
        action={
          <Link to="/boloes">
            <Button variant="outline">Voltar para bolões</Button>
          </Link>
        }
      />
    );
  }

  if (rankingError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Erro ao carregar ranking</AlertTitle>
        <AlertDescription>
          Não foi possível carregar o ranking deste bolão agora.{' '}
          <Link to={`/boloes/${poolSlug}`} className="font-medium underline">
            Voltar para o bolão
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  const filtered = rankings?.filter((r) =>
    (r.user?.name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const top3 = rankings?.slice(0, 3) ?? [];

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        back={
          <Link
            to={`/boloes/${poolSlug}`}
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para {pool?.name ?? 'o bolão'}
          </Link>
        }
        title="Ranking"
        description="Classificação geral dos participantes deste bolão."
      />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-44 rounded-2xl" />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : !rankings || rankings.length === 0 ? (
        <EmptyState
          icon={<Trophy className="w-6 h-6" aria-hidden="true" />}
          title="Nenhum participante ainda"
          description="Convide amigos para o bolão e acompanhe a disputa em tempo real."
        />
      ) : (
        <>
          {/* Top-3 mini-cards */}
          {top3.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[0, 1, 2].map((i) => {
                const place = (i === 0 ? top3[0] : i === 1 ? top3[1] : top3[2]) as
                  | (Ranking & { user?: { name: string; avatarUrl: string | null } })
                  | undefined;
                if (!place) return <div key={i} aria-hidden="true" />;
                const podiumPosition = (i + 1) as 1 | 2 | 3;
                return (
                  <PodiumPlace
                    key={place.userId}
                    name={place.user?.name ?? '—'}
                    points={place.totalPoints}
                    position={podiumPosition}
                  />
                );
              })}
            </div>
          )}

          {/* Search */}
          <div className="relative mb-4">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar participante…"
              aria-label="Buscar participante"
              className="w-full rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 pl-11 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>

          {/* Full ranking */}
          <Card className="overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    #
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Participante
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Exatos
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Vencedores
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Pontos
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      Nenhum participante corresponde à busca.
                    </td>
                  </tr>
                ) : (
                  filtered?.map((ranking) => (
                    <RankingRow key={ranking.userId} ranking={ranking} />
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

const PodiumPlace = memo(function PodiumPlace({
  name,
  points,
  position,
}: {
  name: string;
  points: number;
  position: 1 | 2 | 3;
}) {
  const isFirst = position === 1;
  const tone =
    position === 1
      ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-100'
      : position === 2
        ? 'bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700 text-foreground'
        : 'bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30 text-orange-900 dark:text-orange-100';
  return (
    <div className={cn('rounded-2xl border p-4 flex flex-col items-center gap-2 text-center', tone)}>
      <div className="relative">
        <Avatar className="h-14 w-14 ring-2 ring-white dark:ring-neutral-900">
          <AvatarFallback>{getInitials(name)}</AvatarFallback>
        </Avatar>
        {isFirst && (
          <Crown
            className="absolute -top-3 left-1/2 -translate-x-1/2 h-5 w-5 text-amber-500"
            aria-hidden="true"
          />
        )}
      </div>
      <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{position}º lugar</span>
      <span className="text-sm font-semibold truncate max-w-full">
        {name.split(' ')[0]}
      </span>
      <span className="text-xs text-muted-foreground">{formatPoints(points)}</span>
    </div>
  );
});

const RankingRow = memo(function RankingRow({
  ranking,
}: {
  ranking: Ranking & { user?: { name: string; avatarUrl: string | null }; trend?: string };
}) {
  const trendDisplay = ranking.trend ? getTrendDisplay(ranking.trend) : null;
  const isTop3 = ranking.position <= 3;

  return (
    <tr
      className={cn(
        'border-t border-neutral-200 dark:border-neutral-800 transition-colors',
        isTop3 && 'bg-amber-50/40 dark:bg-amber-500/5'
      )}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'font-bold text-base tabular-nums',
              ranking.position === 1 && 'text-amber-500',
              ranking.position === 2 && 'text-neutral-500 dark:text-neutral-400',
              ranking.position === 3 && 'text-orange-600 dark:text-orange-400',
              ranking.position > 3 && 'text-muted-foreground'
            )}
          >
            {ranking.position}
          </span>
          {trendDisplay && (
            <span className={cn('text-xs', trendDisplay.color)} aria-label={trendDisplay.label}>
              {trendDisplay.icon}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className="text-xs">{getInitials(ranking.user?.name ?? '')}</AvatarFallback>
          </Avatar>
          <span className="font-medium text-foreground truncate">{ranking.user?.name}</span>
        </div>
      </td>
      <td className="hidden sm:table-cell px-4 py-3 text-center text-muted-foreground tabular-nums">
        {ranking.exactScores}
      </td>
      <td className="hidden sm:table-cell px-4 py-3 text-center text-muted-foreground tabular-nums">
        {ranking.correctWinners}
      </td>
      <td className="px-4 py-3 text-right">
        <Badge variant={isTop3 ? 'success' : 'secondary'} className="font-semibold tabular-nums">
          {ranking.totalPoints}
          <span className="opacity-60 ml-0.5">pts</span>
        </Badge>
      </td>
    </tr>
  );
});
